import {Injectable} from '@angular/core';
import {LayerUtilService} from './layer-util.service';
import {MapToolsService} from '../services/map-tools.service';
import {PermissionsService} from '../../common/service/permissions.service';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {SpatialUtilService} from '../services/spatial-util.service';
import {equals} from '../../common/service/data-util';

/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
@Injectable({
    providedIn: 'root'
})
export class AngleLayerService {
    private readonly layerId = 'angleFeatureLayer_layer';

    private readonly layerSelector = `#${this.layerId}`;

    private readonly featureClass = 'angle';

    private readonly featureIdAttrName = this.layerUtilService.getFeatureIdAttrName(this.featureClass);

    private readonly featureTypeAttrName = this.layerUtilService.getFeatureTypeAttrName(this.featureClass);

    private lastScaleFactor = 0;

    private svgStyle: string;

    private trustedSvgStyle: SafeHtml;

    private setCircleTransformsTimeout = null;

    private setCircleTransformsTimeoutFeatureIds = [];

    private mouseDownInfo?: {
        x: number;
        y: number;
        graphic: any;
        pristineGraphic: string;
        graphicX: number;
        graphicY: number;
        bearing: number;
    };

    constructor(
        private domSanitizer: DomSanitizer,
        private layerUtilService: LayerUtilService,
        private mapToolsService: MapToolsService,
        private permissionsService: PermissionsService,
        private spatialUtilService: SpatialUtilService
    ) {}

    getSvgStyle(): SafeHtml {
        const scaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (scaleFactor === 0) {
            return '';
        }

        const backgroundColor = this.layerUtilService.showFeatures()
            ? this.spatialUtilService.getMapBackground() === 'white'
                ? 'black'
                : 'white'
            : 'transparent';

        const mouseOverUseElementSelector = `use[href^="#angle-feature-"]`;

        const styles = [
            `${this.layerSelector} circle[${this.featureIdAttrName}][data-selected] + g[${this.featureIdAttrName}]` +
                ` > text {fill:${this.layerUtilService.getSelectedColor()};}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}][data-hidden]:not([data-selected]) + g[${this.featureIdAttrName}]` +
                ` > text {fill:${this.layerUtilService.getHiddenColor()};}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}] + g[${this.featureIdAttrName}]` +
                ` > text {font-family: Arial; font-size: ${scaleFactor}px; fill: ${backgroundColor};}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}]:hover` +
                ` + g[${this.featureIdAttrName}] {animation: feature-symbol-hover 0.3s alternate ease-in-out none infinite;}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}]:not([data-hidden]):not([data-selected]):hover` +
                ` + g[${this.featureIdAttrName}] > text {fill:${this.layerUtilService.getHoverColor()};}`,

            `${this.layerSelector} circle[${this.featureIdAttrName}][data-hidden]:not([data-selected]):hover` +
                ` + g[${this.featureIdAttrName}] > text > tspan {fill:${this.layerUtilService.getHoverColor()};}`,

            mouseOverUseElementSelector +
                ` {font-family: Arial; font-size: ${scaleFactor}px; fill:${this.layerUtilService.getHoverColor()}; animation: feature-symbol-hover 0.3s alternate ease-in-out none infinite;}`,
        ];

        if (this.mapToolsService.isScaleActive()) {
            styles.push(
                `${this.layerSelector} circle[${this.featureIdAttrName}]${
                    this.mapToolsService.isMoveActive() || this.mapToolsService.isRotateActive() ? '' : '[data-selected]'
                }:hover {cursor:pointer;}`
            );
        }

        const newSvgStyle = styles.join('\n');
        if (newSvgStyle === this.svgStyle) {
            return this.trustedSvgStyle;
        }

        this.lastScaleFactor = scaleFactor;

        this.svgStyle = newSvgStyle;

        this.trustedSvgStyle = this.domSanitizer.bypassSecurityTrustHtml(this.svgStyle);

        return this.trustedSvgStyle;
    }

    init() {
        this.setCircleTransformsTimeout = null;
        this.setCircleTransformsTimeoutFeatureIds.length = 0;

        const angleFeatureLayer = this.spatialUtilService.getAngleMapLayer();

        this.layerUtilService.addLayerEventHandler(angleFeatureLayer, 'graphic-node-add', (evt: {graphic: any; node: any}) => {
            const featureId = evt.graphic.attributes.id;
            // console.info(`${featureClass} - graphic-node-add: feautreid": ${featureId}`);

            this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);

            // remove existing nodes and text if present
            this.removeNodes(featureId);

            this.layerUtilService.setAngleLabelProperties(evt.graphic);

            if (evt.graphic.attributes.labelproperties) {
                // hide the node
                evt.node.setAttribute('style', 'fill-opacity:0;stroke-opacity:0');

                // set the parent node id
                evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);

                this.createTextLabels(evt);
            }
        });

        this.layerUtilService.addLayerEventHandler(angleFeatureLayer, 'graphic-node-remove', (evt: {graphic: any}) => {
            // console.info(`${featureClass} - graphic-node-remove`);
            const featureId = evt.graphic.attributes.id;
            this.removeNodes(featureId);
        });

        this.layerUtilService.addLayerEventHandler(angleFeatureLayer, 'graphics-clear', () => {
            // console.info("graphics-clear");
        });

        this.layerUtilService.addLayerEventHandler(angleFeatureLayer, 'edits-complete', () => {
            const graphicIds = this.spatialUtilService
                .getGraphics(this.featureClass)
                .map((g: any) => g.attributes.id)
                .sort((a: number, b: number) => a - b);

            // console.info(`${featureClass} - edits-complete - graphic ids: ${graphicIds}`);

            const layerElt = document.getElementById(this.layerId);

            const groupNodeFeatureIds = Array.from(layerElt.querySelectorAll(`g[${this.featureIdAttrName}]`)).map(v =>
                parseInt(v.getAttribute(this.featureIdAttrName))
            );

            const circleNodeFeatureIds = Array.from(layerElt.querySelectorAll(`circle[${this.featureIdAttrName}]`)).map(v =>
                parseInt(v.getAttribute(this.featureIdAttrName))
            );

            const nodeFeatureIds = [...groupNodeFeatureIds, ...circleNodeFeatureIds].filter((v, i, a) => i === a.indexOf(v)).sort((a, b) => a - b);

            // console.info(`${featureClass} - edits-complete - node feature ids: ${nodeFeatureIds}`);

            const {nodeFeatureIdsToKeep, nodeFeatureIdsToRemove} = nodeFeatureIds.reduce(
                (acc, v) => {
                    if (graphicIds.includes(v)) {
                        acc.nodeFeatureIdsToKeep.push(v);
                    } else {
                        acc.nodeFeatureIdsToRemove.push(v);
                    }
                    return acc;
                },
                {nodeFeatureIdsToKeep: [], nodeFeatureIdsToRemove: []}
            );

            nodeFeatureIdsToRemove.forEach(v => this.removeNodes(v));

            this.setCircleTransforms(nodeFeatureIdsToKeep);
        });

        this.layerUtilService.addLayerEventHandler(angleFeatureLayer, 'graphic-draw', (evt: {graphic: any; node: any}) => {
            const graphic = evt.graphic;
            const featureId = graphic.attributes.id;

            this.layerUtilService.setHiddenAttr(evt.node, this.featureClass, featureId);

            const {cx, cy} = this.layerUtilService.getGraphicShape(graphic);

            document.querySelectorAll(`${this.layerSelector} > g[${this.featureIdAttrName}="${featureId}"]`).forEach(g => {
                const text = g.querySelector('text');
                if (text) {
                    const pointOffset = this.layerUtilService.getPointOffset(cx, cy, text.style.textAnchor, graphic, 'angle');
                    const x = pointOffset.x || 0;
                    const y = pointOffset.y || 0;

                    g.setAttribute(
                        'transform',
                        `translate(${x}, ${y}) rotate(${this.layerUtilService.getSvgRotation(graphic.attributes.anglerotation)})`
                    );

                    this.setCircleTransform(featureId, pointOffset);

                    this.layerUtilService.setGraphicExtentToDisplayNodeExtent(this.featureClass, graphic);
                }
            });
        });

        this.layerUtilService.addLayerEventHandler(angleFeatureLayer, 'click', (evt: any) => {
            this.layerUtilService.onClick(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(angleFeatureLayer, 'dbl-click', (evt: {graphic: any; target: any}) => {
            this.layerUtilService.setSelectedAttr(evt.target, this.featureClass, evt.graphic.attributes.id);
        });

        this.layerUtilService.addLayerEventHandler(angleFeatureLayer, 'mouse-over', (evt: any) => {
            this.layerUtilService.onMouseOver(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(angleFeatureLayer, 'mouse-out', (evt: any) => {
            this.layerUtilService.onMouseOut(this.featureClass, evt);
        });

        this.layerUtilService.addLayerEventHandler(
            angleFeatureLayer,
            'mouse-down',
            (evt: {graphic: any; mapPoint: any; which: any; altKey: any; ctrlKey: any; shiftKey: any}) => {
                if (
                    evt.which !== 1 ||
                    evt.altKey ||
                    evt.ctrlKey ||
                    evt.shiftKey ||
                    !this.mapToolsService.isScaleActive() ||
                    this.mapToolsService.isActive('DISPLAY_MODE_PRINT_PREVIEW') ||
                    !this.permissionsService.hasEditPermission(['EDIT_DESIGN'])
                ) {
                    return;
                }

                const {x, y} = evt.mapPoint;
                const graphic = evt.graphic;

                this.layerUtilService.suspendDragDrawTools(this.featureClass, graphic.attributes.id);

                this.mouseDownInfo = {
                    x: x,
                    y: y,
                    graphic: graphic,
                    pristineGraphic: evt.graphic.toJson(),
                    graphicX: graphic.geometry.x,
                    graphicY: graphic.geometry.y,
                    bearing: this.layerUtilService.calcBearing(graphic.geometry.x, graphic.geometry.y, x, y) - (graphic.attributes.anglerotation || 0)
                };
            }
        );

        const map = this.spatialUtilService.getMap();

        map.on('mouse-drag', (evt: {mapPoint: any}) => {
            if (!this.mouseDownInfo) {
                return;
            }

            const graphic = this.mouseDownInfo.graphic;

            if (
                this.mapToolsService.isMoveActive() ||
                (!this.mapToolsService.isRotateActive() && this.spatialUtilService.isHighlightedFeature(this.featureClass, graphic.attributes.id))
            ) {
                graphic.geometry.x = graphic.attributes.x = evt.mapPoint.x + this.mouseDownInfo.graphicX - this.mouseDownInfo.x;
                graphic.geometry.y = graphic.attributes.y = evt.mapPoint.y + this.mouseDownInfo.graphicY - this.mouseDownInfo.y;

                graphic.draw();
            } else if (
                this.mapToolsService.isRotateActive() &&
                this.spatialUtilService.isHighlightedFeature(this.featureClass, graphic.attributes.id)
            ) {
                // Rotate feature will not allow user to move the annotation after selecting it.
                graphic.geometry.x = graphic.attributes.x = evt.mapPoint.x + this.mouseDownInfo.graphicX - this.mouseDownInfo.x;
                graphic.geometry.y = graphic.attributes.y = evt.mapPoint.y + this.mouseDownInfo.graphicY - this.mouseDownInfo.y;

                graphic.draw();
            } else if (
                this.mapToolsService.isRotateActive() &&
                !this.spatialUtilService.isHighlightedFeature(this.featureClass, graphic.attributes.id)
            ) {
                const oldRotation = (graphic.attributes.anglerotation || 0) % 360;
                const bearing = this.layerUtilService.calcBearing(graphic.geometry.x, graphic.geometry.y, evt.mapPoint.x, evt.mapPoint.y);
                const newRotation = (Math.round(bearing - this.mouseDownInfo.bearing) + 360) % 360;

                graphic.attributes.anglerotation = newRotation;
                graphic.attributes.anglerotationdms = `${newRotation}`;

                if (this.getAngleTextAnchor(oldRotation) !== this.getAngleTextAnchor(newRotation)) {
                    const text: any = document.querySelector(
                        `${this.layerSelector} > g[${this.featureIdAttrName}="${graphic.attributes.id}"] > text`
                    );
                    if (text) {
                        text.style.textAnchor = text.style.textAnchor === 'start' ? 'end' : 'start';
                    }
                }

                graphic.draw();
            }
        });

        map.on('mouse-out', () => this.onMouseDragEnd());

        map.on('mouse-up', () => this.onMouseDragEnd());

        return angleFeatureLayer;
    }

    private getAngleTextAnchor(bearing: number) {
        bearing = bearing ? bearing % 360 : 0;

        // Anchor needs to match server-side rendering (used in print-preview mode)

        if (bearing >= 1 && bearing < 181) {
            return 'start';
        }

        return 'end';
    }

    private createTextLabels(evt: {graphic: any; node: any}) {
        const featureId = evt.graphic.attributes.id;

        evt.node.setAttribute(this.featureTypeAttrName, `${evt.graphic.attributes.type}`);
        evt.node.setAttribute(this.featureIdAttrName, `${featureId}`);
        this.layerUtilService.setSelectedAttr(evt.node, this.featureClass, featureId);

        const {cx, cy} = this.layerUtilService.getGraphicShape(evt.graphic);

        const text: any = document.createElementNS('http://www.w3.org/2000/svg', 'text');
        const g: any = document.createElementNS('http://www.w3.org/2000/svg', 'g');

        g.setAttribute('id', this.layerUtilService.getFeatureNodeId(this.featureClass, featureId));
        g.setAttribute(this.featureIdAttrName, `${featureId}`);
        g.setAttribute('pointer-events', 'none');

        const pointOffset = this.layerUtilService.getPointOffset(cx, cy, text.textAnchor, evt.graphic, 'angle');
        const x = pointOffset.x || 0;
        const y = pointOffset.y || 0;

        g.setAttribute('transform', `translate(${x}, ${y}) rotate(${this.layerUtilService.getSvgRotation(evt.graphic.attributes.anglerotation)})`);

        const angleLayerNode = document.getElementById(this.layerId);
        angleLayerNode.insertBefore(g, evt.node.nextSibling);

        text.setAttribute(this.featureTypeAttrName, `${evt.graphic.attributes.type}`);
        text.style.textAnchor = this.getAngleTextAnchor(evt.graphic.attributes.anglerotation);
        text.style.dominantBaseline = 'middle';

        text.setAttribute('dx', `${text.style.textAnchor === 'end' ? '-0.99em' : '1.01em'}`);
        text.setAttribute('dy', '1.01em');

        const labels = JSON.parse(evt.graphic.attributes.labelproperties);
        labels.map((l: any) => {
            const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');

            tspan.setAttribute('x', '0');
            tspan.setAttribute('y', '0');

            tspan.style.fontSize = l.style.fontSize;
            tspan.style.fontStyle = l.style.fontStyle;
            tspan.textContent = l.value;
            text.appendChild(tspan);
        });

        evt.node.parentNode.appendChild(text);
        g.appendChild(text);
    }

    private removeNodes(featureId: number) {
        const layerElt = document.getElementById(this.layerId);

        layerElt.querySelectorAll(`g[${this.featureIdAttrName}="${featureId}"]`).forEach(g => g.parentNode.removeChild(g));

        const circle = layerElt.querySelector(`circle[${this.featureIdAttrName}="${featureId}"]`);
        if (circle) {
            circle.parentNode.removeChild(circle);
        }
    }

    private setCircleTransform(featureId: number, pointOffset?: {x: number; y: number}) {
        const layerElt = document.getElementById(this.layerId);

        const circle: any = layerElt.querySelector(`circle[${this.featureIdAttrName}="${featureId}"]`);
        if (!circle || !circle.e_graphic) {
            return;
        }

        const g: any = layerElt.querySelector(`g[${this.featureIdAttrName}="${featureId}"]`);
        if (!g) {
            return;
        }

        const text = g.querySelector('text');
        if (!text) {
            return;
        }

        const graphic = circle.e_graphic;

        const shape = graphic.getShape().shape;
        const cx = shape.cx;
        const cy = shape.cy;

        const scaleFactor = this.layerUtilService.getPixelScaleFactor();
        if (scaleFactor === 0) {
            return;
        }

        const circleBBox = circle.getBBox();
        if (circleBBox.width === 0 || circleBBox.height === 0) {
            return;
        }

        const gBBox = g.getBBox();
        if (gBBox.width === 0 || gBBox.height === 0) {
            if (!this.setCircleTransformsTimeoutFeatureIds.includes(featureId)) {
                this.setCircleTransformsTimeoutFeatureIds.push(featureId);
            }
            if (this.setCircleTransformsTimeout) {
                clearTimeout(this.setCircleTransformsTimeout);
            }
            // Use native setTimeout method instead of $timeout to avoid triggering angular's digest cycle
            this.setCircleTransformsTimeout = setTimeout(() => this.setCircleTransforms(this.setCircleTransformsTimeoutFeatureIds.slice()), 1000);
            return;
        }

        const featureIdx = this.setCircleTransformsTimeoutFeatureIds.indexOf(featureId);
        if (featureIdx !== -1) {
            this.setCircleTransformsTimeoutFeatureIds.splice(featureIdx, 1);
        }

        let sx = gBBox.width / circleBBox.width;
        let sy = gBBox.height / circleBBox.height;

        if (this.lastScaleFactor && this.lastScaleFactor !== scaleFactor) {
            sx *= scaleFactor / this.lastScaleFactor;
            sy *= scaleFactor / this.lastScaleFactor;
        }

        const rotation = this.layerUtilService.getSvgRotation(graphic.attributes.anglerotation);
        const tx = cx - (sx * cx + ((text.style.textAnchor === 'start' ? -1 : 1) * sx * circleBBox.width) / 2);
        const ty = cy - sy * cy;

        const transformDefinitions = [];
        if (!this.mapToolsService.isScaleActive() && pointOffset) {
            const translateX = pointOffset.x - cx;
            const translateY = pointOffset.y - cy;
            if (translateX || translateY) {
                transformDefinitions.push(`translate(${translateX}, ${translateY})`);
            }
        }
        transformDefinitions.push(`rotate(${rotation}, ${cx}, ${cy}) matrix(${sx}, 0, 0, ${sy}, ${tx}, ${ty})`);

        circle.setAttribute('transform', transformDefinitions.join(' '));
        circle.setAttribute('pointer-events', 'bounding-box');
    }

    private setCircleTransforms(featureIds: number[]) {
        featureIds.forEach(featureId => this.setCircleTransform(featureId));
    }

    private onMouseDragEnd() {
        if (this.mouseDownInfo) {
            if (!equals(this.mouseDownInfo.pristineGraphic, this.mouseDownInfo.graphic.toJson())) {
                const {anglerotation: angleRotation, id: featureId} = this.mouseDownInfo.graphic.attributes;

                this.spatialUtilService.applyEdits(this.featureClass, null, [this.mouseDownInfo.graphic], null).subscribe(() => {
                    const text: any = document.querySelector(`${this.layerSelector} > g[${this.featureIdAttrName}="${featureId}"] > text`);
                    if (text) {
                        text.style.textAnchor = this.getAngleTextAnchor(angleRotation);
                        this.setCircleTransform(featureId);
                    }
                });
            }

            this.layerUtilService.restoreDragDrawTools();
            this.mouseDownInfo = null;
        }
    }
}
